import { ComponentType, useContext, useEffect, useMemo, useState } from "react";
import { Location, Navigate, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import Spinner from "src/components/Spinner";
import { useAnonymousPathPatterns } from "src/pages/hooks/anonymous-path-patterns";
import { agreementCheckResultState, isJustLoggedInState } from "src/recoil/auth/atom";
import { convertToPartnerUrl } from "src/utils/common-util";
import log from "src/utils/log";
import { route } from "src/vars";
import CourtAuthContext, { CourtAuthReturnTo, CourtAuthReturnToState, CourtAuthState } from "../contexts/CourtAuthContext";
import useCourtAuth from "../hooks/court-auth";
import courtAuthStateRepository from "../utils/court-auth-state-repository";

export type WithAuthRequiredOptions = {
  // 인증 후 찾아갈 경로 (함수인 경우 인증이 필요하다고 판정된 시점의 location 을 전달받는다)
  returnTo?: string | ((location: Location) => string);
  // 인증 필요 시 출력 컴포넌트
  Unauthenticated?: () => JSX.Element;
};

// 복귀 경로는 반드시 라우터 location 으로 계산한다.
// 복귀 경로를 저장하는 effect 는 자식 <Navigate> 의 effect 이후에 실행되므로,
// 그 시점에 window.location 을 읽으면 이미 이동한 로그인 경로가 저장된다.
const defaultReturnTo = (location: Location) => `${route.basename}${location.pathname}${location.search}`;

export default function withAuthRequired<P extends JSX.IntrinsicAttributes>(Component: ComponentType<P>, options: WithAuthRequiredOptions = {}) {
  return (props: P) => {
    const { returnTo = defaultReturnTo, Unauthenticated = () => <></> } = options;
    const { security } = useContext(CourtAuthContext);
    const { revoke, authorize } = useCourtAuth();
    const isJustLoggedIn = useRecoilValue(isJustLoggedInState);
    const agreementResult = useRecoilValue(agreementCheckResultState);

    // 무인증 페이지 여부
    const { matchAnonymous } = useAnonymousPathPatterns();
    const location = useLocation();

    const [isReady, setIsReady] = useState(false);

    // 이메일 등록 + 약관 동의 미완료 여부
    // ROLE_STARTER 는 정식 회원(MEMBER)이 아니므로 이메일 등록(/join/email) 또는
    // 약관 페이지(/join/agreements)에서 STARTER => MEMBER 처리와 약관 동의를 마쳐야 한다.
    // 두 페이지는 온보딩의 목적지이므로 판정에서 제외한다.
    const isRequireJoin = useMemo(() => {
      return (
        !matchAnonymous &&
        !location.pathname.includes("join/email") &&
        !location.pathname.includes("join/agreements") &&
        security.status === "authenticated" &&
        security.user?.roles.includes("ROLE_STARTER")
      );
    }, [matchAnonymous, location, security]);

    // 인증 필요 상태 여부
    const isRequireAuth = useMemo(() => {
      console.log(matchAnonymous, isRequireJoin, ["none", "pending", "authenticated"], security.status);
      return !matchAnonymous && !isRequireJoin && !["none", "pending", "authenticated"].includes(security.status);
    }, [matchAnonymous, isRequireJoin, security]);

    // 약관 미동의 상태로 온보딩을 벗어난 경우
    const isLeftOnboarding = useMemo(() => {
      if (matchAnonymous || isJustLoggedIn || security.status !== "authenticated") return false;
      if (location.pathname.includes("join/email") || location.pathname.includes("join/agreements")) return false;
      const isStarter = !!security.user?.roles.includes("ROLE_STARTER");
      const isMemberAgreementMissing =
        !isStarter && !!agreementResult && agreementResult.memberNo === security.user?.memberNo && agreementResult.required;
      return isStarter || isMemberAgreementMissing;
    }, [matchAnonymous, isJustLoggedIn, security, location, agreementResult]);

    useEffect(() => {
      if (isRequireAuth) {
        // 인증 페이지 인데 인증상태 아닐 경우
        const orgState = courtAuthStateRepository.load() ?? ({} as CourtAuthState);
        const orgHistory = orgState.returnTo?.history ?? [];
        const latest = {
          uri: typeof returnTo === "function" ? returnTo(location) : returnTo,
          time: Math.floor(new Date().getTime() / 1000),
        } as CourtAuthReturnTo;
        const returnToState = { latest, history: [latest, ...orgHistory] } as CourtAuthReturnToState;
        const newState = { returnTo: returnToState };
        log("withAuthRequired :: isRequireAuth :: newState", JSON.stringify(newState, null, 2));
        courtAuthStateRepository.save(newState);
        // // 인증 필요
        // setRequireAuth(true);
      }

      if (!isReady) {
        setIsReady(true);
      }
    }, [isRequireAuth, returnTo, location]);

    // 약관 미동의로 온보딩을 벗어난 경우: 세션(토큰·복귀경로)을 폐기하고 로그인 프로세스로 보낸다.
    useEffect(() => {
      if (!isLeftOnboarding) return;
      (async () => {
        try {
          await revoke();
        } finally {
          authorize({ replace: true });
        }
      })();
    }, [isLeftOnboarding, revoke, authorize]);

    log("withAuthRequired :: isRequireAuth", isRequireAuth);
    log("withAuthRequired :: isRequireJoin", isRequireJoin);

    if (!isReady) {
      return <></>;
    }

    const isEmailVerified = security.user?.email?.isVerified;
    const isPhoneVerified = security.user?.phoneNumber?.isVerified;

    // 세션 폐기 + 재로그인 진행 중
    if (isLeftOnboarding) {
      return (
        <div className="spinner-container">
          <Spinner size="page" />
        </div>
      );
    }

    if (isRequireJoin) {
      // 이메일 미인증 + 번호인증됨 + 로그인 직후: 이메일 등록 페이지
      if (!isEmailVerified && isPhoneVerified && isJustLoggedIn) {
        return <Navigate to={convertToPartnerUrl(location.pathname, "/court/join/email")} replace />;
      }
      // 이메일 인증된 STARTER: 약관 페이지에서 STARTER => MEMBER 처리 + 약관 동의
      if (isEmailVerified) {
        return <Navigate to={convertToPartnerUrl(location.pathname, "/court/join/agreements")} replace />;
      }
      // 그 외: 로그인 페이지로
      return <Navigate to={convertToPartnerUrl(location.pathname, "/court/login")} replace />;
    }

    if (isRequireAuth) {
      // 인증 페이지 인데 인증 필요할 경우
      return <Unauthenticated />;
    }

    if (security.status !== "authenticated") {
      // 인증 페이지인데 인증 상태가 아닐 경우
      return <></>;
    }

    // 약관 체크 대상: 가입 완료(ROLE_MEMBER) & 무인증 페이지 아님 & 약관 페이지 자신이 아님
    const isAgreementCheckTarget =
      !matchAnonymous && !security.user?.roles.includes("ROLE_STARTER") && !location.pathname.includes("join/agreements");

    if (isAgreementCheckTarget) {
      // 현재 회원의 약관 체크 결과가 없으면 진입 보류
      const isAgreementResolved = !!agreementResult && agreementResult.memberNo === security.user?.memberNo;
      if (!isAgreementResolved) {
        return (
          <div className="spinner-container">
            <Spinner size="page" />
          </div>
        );
      }
      // 미동의 약관 존재 시 약관 동의 페이지로 강제 이동
      if (agreementResult.required) {
        return <Navigate to={convertToPartnerUrl(location.pathname, "/court/join/agreements")} replace />;
      }
    }

    // 무인증 페이지 또는 인증페이지 인증 완료된 경우
    return <Component {...props} />;
  };
}
